import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const role = String(user?.role || '').toLowerCase();

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('crm_token');
    if (token) {
      try {
        const res = await authAPI.getMe();
        setUser(res.data);
      } catch {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        setUser(null);
      }
    } else {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({
      email: String(email ?? '').trim(),
      password
    });
    const token = res.data.token;
    localStorage.setItem('crm_token', token);
    try {
      const me = await authAPI.getMeWithToken(token);
      setUser(me.data);
      localStorage.setItem('crm_user', JSON.stringify(me.data));
    } catch (err) {
      const status = err.response?.status;
      // Bad/expired token right after login — full reset
      if (status === 401 || status === 403) {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        setUser(null);
        throw err;
      }
      // Profile load failed (e.g. transient 500): keep JWT, use login payload so session still works
      const fallback = {
        ...res.data.user,
        permissions: [],
        campus_access: []
      };
      setUser(fallback);
      localStorage.setItem('crm_user', JSON.stringify(fallback));
    }
    return res.data;
  }, []);

  const logout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    setUser(null);
  };

  const isSuperAdmin = role === 'super_admin';
  const isAdmin = !!user && ['super_admin', 'admin', 'sales_manager', 'manager'].includes(role);
  const permissionDrivenRoles = useMemo(
    () => new Set(['admin', 'manager', 'sales_manager', 'sales_executive', 'company_relation']),
    []
  );
  const actionMap = useMemo(
    () => ({ read: 'can_read', create: 'can_create', update: 'can_update', delete: 'can_delete' }),
    []
  );
  const getPermissionForModule = useCallback(
    (module) => (user?.permissions || []).find((p) => p.module === module),
    [user]
  );

  const hasModuleAccess = useCallback((module) => {
    if (!user) return false;
    if (role === 'super_admin') return true;
    if (permissionDrivenRoles.has(role)) {
      return !!getPermissionForModule(module)?.can_read;
    }
    if (role === 'interviewer') {
      if (module === 'dashboard') return true;
      if (['interviews', 'mock_interviews', 'students', 'tickets'].includes(module)) {
        return (user.campus_access || []).length > 0;
      }
      return false;
    }
    return false;
  }, [user, role, permissionDrivenRoles, getPermissionForModule]);

  const canPerformAction = useCallback((module, action) => {
    if (!user) return false;
    if (role === 'super_admin') return true;
    if (permissionDrivenRoles.has(role)) {
      const perm = getPermissionForModule(module);
      if (!perm) return false;
      const key = actionMap[action];
      if (!key) return false;
      return !!perm[key];
    }
    if (role === 'interviewer') {
      if (module === 'students') return ['read', 'update'].includes(action);
      if (['interviews', 'mock_interviews'].includes(module)) {
        return ['read', 'create', 'update'].includes(action);
      }
      if (['activities', 'meetings'].includes(module)) return action === 'read';
      if (module === 'tickets') return ['read', 'create', 'update'].includes(action);
      if (module === 'dashboard') return action === 'read';
      return false;
    }
    return false;
  }, [user, role, permissionDrivenRoles, getPermissionForModule, actionMap]);

  const contextValue = useMemo(() => ({
    user, loading, login, logout,
    isAdmin, isSuperAdmin,
    hasModuleAccess, canPerformAction,
    loadUser
  }), [user, loading, login, logout, isAdmin, isSuperAdmin, hasModuleAccess, canPerformAction, loadUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
